<template>
  <div class="policy-treemap m-chart-block__container--default-highlight">
    <Chart :options="chartOptions" />
    <p class="f-ui-3">{{ title }}</p>
  </div>
</template>

<script>
/* global Highcharts */
import ChartOptions from "@international-energy-agency/vue-ui/highcharts";
import { Chart } from "highcharts-vue";
import g20 from "@/assets/g20.json";
import members from "@/assets/members.json";
export default {
  props: {
    policies: {
      type: Array,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
    topic: {
      type: String,
      required: true,
    },
    family: {
      type: String,
      required: true,
    },
    policyType: {
      type: String,
      required: true,
    },
    category: {
      type: String,
      required: false,
    },
    width: {
      type: Number,
      default: 280,
    },
  },
  components: {
    Chart,
  },
  computed: {
    title() {
      const { topic, family, policyType, category, data } = this;
      const firstRow = data.find(
        (row) =>
          row.topic === topic &&
          (family === "All" || row.family === family) &&
          row.policyType === policyType &&
          (!category || category === "" || row.category === category)
      );
      return firstRow && typeof firstRow.label === "string"
        ? firstRow?.label.replace("Notes: ", "")
        : "";
    },
    treeSeries() {
      const {
        topic,
        family,
        policyType,
        category,
        inForceCountries,
        announcedCountries,
        data,
      } = this;
      const relevantData = data.filter(
        (row) =>
          row.topic === topic &&
          (family === "All" || row.family === family) &&
          row.policyType === policyType &&
          (!category || row.category === category)
      );
      const regions = [...g20, ...members];
      return [
        {
          name: "In force",
          id: "in-force",
          data: [
            {
              y: relevantData
                .filter(
                  (row) =>
                    inForceCountries.includes(row.country) &&
                    regions.includes(row.country)
                )
                .reduce((total, row) => total + row.value, 0),
              colorIndex: 0,
            },
          ],
        },
        {
          name: "Announced",
          id: "anounced",
          data: [
            {
              y: relevantData
                .filter(
                  (row) =>
                    announcedCountries.includes(row.country) &&
                    regions.includes(row.country)
                )
                .reduce((total, row) => total + row.value, 0),
              colorIndex: 1,
            },
          ],
        },
        {
          name: "Not covered",
          id: "not-covered",
          data: [
            {
              y: relevantData
                .filter(
                  (row) =>
                    row.country !== "World" &&
                    !inForceCountries.includes(row.country) &&
                    !announcedCountries.includes(row.country) &&
                    regions.includes(row.country)
                )
                .reduce((total, row) => total + row.value, 0),
              colorIndex: 98,
            },
          ],
        },
        {
          name: "Rest of the world",
          id: "row",
          data: [
            {
              y: relevantData
                .filter(
                  (row) =>
                    row.country !== "World" &&
                    !inForceCountries.includes(row.country) &&
                    !announcedCountries.includes(row.country) &&
                    !regions.includes(row.country)
                )
                .reduce((total, row) => total + row.value, 0),
              colorIndex: 99,
            },
          ],
        },
      ];
    },
    chartOptions() {
      const { policyType, category } = this;
      return ChartOptions.buildOptions({
        chart: {
          type: "bar",
          height: 38,
          spacing: 0,
          margin: [0,10,0,0]
        },
        xAxis: {
          visible: false,
        },
        yAxis: {
          visible: false,
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          bar: {
            stacking: "percent",
            dataLabels: {
              format: "{point.percentage:.0f}%",
            },
            groupPadding: 0
          },
        },
        tooltip: {
          formatter: function () {
            if (this.series.name === 'Rest of the world') return `
               Rest of the world: <b>${this.percentage.toFixed(2)}%</b>
            `;
            if (this.series.name === 'Not covered') return `
               <b>${this.percentage.toFixed(2)}%</b> not covered by policies
            `;
            return `
               <b>${this.percentage.toFixed(2)}%</b> covered by ${this.series.name.toLowerCase()} policies
            `;
          },
          outside: true
        },
        series: this.treeSeries,
      });
    },
    relevantPolicies() {
      const { category } = this;
      return category
        ? this.policies.filter(
            (policy) => policy.category && policy.category.includes(category)
          )
        : this.policies;
    },
    inForceCountries() {
      return this.relevantPolicies.reduce(
        (countries, policy) =>
          !countries.includes(policy.country) &&
          ["In force", "Achieved"].includes(policy.status)
            ? [...countries, policy.country]
            : countries,
        []
      );
    },
    announcedCountries() {
      const { inForceCountries } = this;
      return this.relevantPolicies.reduce(
        (countries, policy) =>
          !countries.includes(policy.country) &&
          !inForceCountries.includes(policy.country) &&
          policy.status === "Announced"
            ? [...countries, policy.country]
            : countries,
        []
      );
    },
    units() {
      return this.data[0].unit;
    },
  },
  methods: {
    hasInForcePolicy(row) {
      return this.policies.some(
        (policy) =>
          policy.country === row.country &&
          (policy.status === "In force" || policy.status === "Achieved")
      );
    },
    hasAnnouncedPolicy(row) {
      return this.policies.some(
        (policy) =>
          policy.country === row.country &&
          (policy.status === "In force" || policy.status === "Achieved")
      );
    },
  },
};
</script>

<style>
.policy-treemap {
  width: 100%;
  overflow: visible;
}
.policy-treemap .f-ui-3 {
  color: #949494;
}
.policy-treemap .highcharts-treemap-series .highcharts-point {
  stroke: #fff;
}
.policy-treemap .highcharts-point.highcharts-color-1, .highcharts-label .highcharts-point.highcharts-color-1 {
  fill: #3b6fff;
  stroke: #3b6fff;
}
.policy-treemap .highcharts-point.highcharts-color-0, .highcharts-label .highcharts-point.highcharts-color-0 {
  stroke: #04f;
  fill: #04f;
}
.policy-treemap .highcharts-point.highcharts-color-98 {
  fill: #d3d3d3;
  stroke: #d3d3d3;
}
.policy-treemap .highcharts-point.highcharts-color-99, .highcharts-label .highcharts-point.highcharts-color-99 {
  fill: #f2f2f2;
  stroke: #f2f2f2;
}
</style>
